import { registerPlugin } from '@capacitor/core'
import { platform } from '@/services/platform'

export type AuthorizationStatus =
  | 'authorized'
  | 'denied'
  | 'notDetermined'
  | 'restricted'
  | 'notRequired'
  | 'unknown'

interface TrackingAuthorizationPlugin {
  getStatus(): Promise<{ status: AuthorizationStatus }>
}

const trackingAuthorizationPlugin = registerPlugin<TrackingAuthorizationPlugin>(
  'TrackingAuthorization',
)

/**
 * Get the iOS tracking authorization.
 *
 * Return values can be:
 * - `authorized` - The user authorized tracking.
 * - `denied` - The user denied tracking.
 * - `notDetermined` - The user has not authorized nor denied tracking yet
 *   (this can happen when the authorization popup is still open, for example).
 * - `restricted` - The device has a setting to deny all tracking requests by default.
 * - `unknown` - Apple introduced some new value that we weren't aware of.
 * - `notRequired` - The device is below iOS 14, which does not require tracking authorization.
 * - `null` - The device is not iOS.
 */
export async function getIosTrackingAuthorization(): Promise<AuthorizationStatus | null> {
  const isIOS = await platform.isNativeIOSApp()
  if (!isIOS) {
    return null
  }

  // This is wrapped in a try/catch block because on older versions
  // of the app, the plugin won't exist and therefore will throw an error.
  try {
    const { status } = await trackingAuthorizationPlugin.getStatus()
    return status
  } catch (error) {
    return 'denied'
  }
}
